import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../../components/Modal';
import { backendErrorNotification, frontendNotification } from '../../../../../components/Notification';
import { tableExtensionsApi, tableExtensionOptionsApi } from '../../../../../services/requests';
import { formatDefaultValue, parseDefaultValue, settingsForType } from '../form';
import { ExtensionOptions, FieldSettings, FieldType, TableExtension } from '../types';

type Props = { item: TableExtension | null; close: () => void; onSuccess: () => void };

const TableExtensionModal = ({ item, close, onSuccess }: Props) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<ExtensionOptions | null>(null);
  const [definition, setDefinition] = useState(item);
  const [tableName, setTableName] = useState(item?.table_name ?? '');
  const [relationship, setRelationship] = useState(item?.relationship_columns ?? []);
  const [fieldName, setFieldName] = useState(item?.field_name ?? '');
  const [type, setType] = useState<FieldType>(item?.field_type ?? 'text');
  const [settings, setSettings] = useState<FieldSettings>(item?.field_settings ?? settingsForType('text'));
  const [description, setDescription] = useState(item?.description ?? '');
  const [required, setRequired] = useState(item?.required ?? false);
  const [hasDefault, setHasDefault] = useState(item ? item.default_value !== null : false);
  const [defaultText, setDefaultText] = useState(item?.field_type === 'datetime'
    ? formatDefaultValue(item.default_value).replace(' ', 'T') : formatDefaultValue(item?.default_value ?? null));
  const [optionText, setOptionText] = useState(item?.field_settings.options?.join('\n') ?? '');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let active = true;
    tableExtensionOptionsApi.query = '';
    tableExtensionsApi.query = '';
    Promise.all([tableExtensionOptionsApi.index(), item ? tableExtensionsApi.show(item.id) : Promise.resolve(null)])
      .then(([optionResponse, definitionResponse]) => {
        if (!active) return;
        setOptions(optionResponse.data.data);
        if (definitionResponse) {
          const current: TableExtension = definitionResponse.data.data;
          setDefinition(current);
          setTableName(current.table_name);
          setRelationship(current.relationship_columns);
          setFieldName(current.field_name);
          setType(current.field_type);
          setSettings(current.field_settings);
          setDescription(current.description);
          setRequired(current.required);
          setHasDefault(current.default_value !== null);
          setDefaultText(current.field_type === 'datetime'
            ? formatDefaultValue(current.default_value).replace(' ', 'T') : formatDefaultValue(current.default_value));
          setOptionText(current.field_settings.options?.join('\n') ?? '');
        }
      }).catch((error: AxiosError) => {
        if (!active) return;
        setLoadFailed(true);
        backendErrorNotification(error);
      }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [item]);

  const locked = Boolean(definition?.has_values);
  const selectedTable = options?.tables.find((table) => table.table_name === tableName);
  const choices = optionText.split('\n').map((value) => value.trim()).filter(Boolean);
  const errorFor = (field: string) => Object.entries(errors)
    .filter(([key]) => key === field || key.startsWith(`${field}.`))
    .flatMap(([, messages]) => messages).join(' ');
  const error = (field: string) => errorFor(field) && <p className="table-extension-error">{errorFor(field)}</p>;

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setErrors({});
    try {
      tableExtensionsApi.query = '';
      const payload = {
        table_name: tableName, relationship_columns: relationship, field_name: fieldName.trim(), field_type: type,
        field_settings: type === 'select' || type === 'multi_select' ? { options: choices } : settings,
        description: description.trim(), required, default_value: parseDefaultValue(type, hasDefault, defaultText),
      };
      if (item) await tableExtensionsApi.update(payload, item.id);
      else await tableExtensionsApi.store(payload);
      frontendNotification({ message: t('tableExtensionSaved'), type: 'success' });
      onSuccess();
      close();
    } catch (err) {
      const apiError = err as AxiosError<{ errors?: Record<string, string[]> }>;
      setErrors(apiError.response?.data.errors ?? {});
      backendErrorNotification(apiError);
    } finally { setSaving(false); }
  };

  return (
    <Modal title={t(item ? 'editTableExtension' : 'createTableExtension')} showModal closeModal={close}
      cancelButton={close} action={save} actionNameBtn={t('save')} isLoading={saving}
      disableActionBtn={loading || loadFailed || !fieldName.trim() || !description.trim() || !relationship.length}
      style={{ maxWidth: '850px', width: '95vw' }}
      bodyStyle={{ overflowY: 'auto', padding: '5rem 1rem', boxSizing: 'border-box' }}>
      {loading && <p>{t('loading')}</p>}
      {loadFailed && <p role="alert">{t('tableExtensionLoadFailed')}</p>}
      {!loading && !loadFailed && (
        <div className="table-extension-form">
          {locked && <p className="table-extension-note full-width">{t('tableExtensionLocked')}</p>}
          <div className="inputWithLabel">
            <label className="label" htmlFor="extension-table">{t('table')}</label>
            <select id="extension-table" className="input" value={tableName} disabled={locked || saving}
              onChange={(event) => {
                const table = options?.tables.find((entry) => entry.table_name === event.target.value);
                setTableName(event.target.value);
                setRelationship(table?.relationship_keys[0]?.columns ?? []);
              }}>
              <option value="">{t('select')}</option>
              {options?.tables.map((table) => (
                <option key={table.table_name} value={table.table_name} disabled={!table.available}>
                  {t(table.label)} ({table.table_name})
                  {!table.available ? ` — ${t('unavailable')}` : ''}
                </option>
              ))}
            </select>{error('table_name')}
          </div>
          <div className="inputWithLabel">
            <label className="label" htmlFor="extension-key">{t('tableExtensionRecordKey')}</label>
            <select id="extension-key" className="input" value={JSON.stringify(relationship)} disabled={locked || saving}
              onChange={(event) => setRelationship(JSON.parse(event.target.value))}>
              <option value="[]">{t('select')}</option>
              {selectedTable?.relationship_keys.map((key) => (
                <option key={key.name} value={JSON.stringify(key.columns)}>{key.columns.join(' + ')}</option>
              ))}
            </select>{error('relationship_columns')}
          </div>
          <div className="inputWithLabel">
            <label className="label" htmlFor="extension-name">{t('tableExtensionFieldName')}</label>
            <input id="extension-name" className="input" value={fieldName} maxLength={64} disabled={locked || saving}
              onChange={(event) => setFieldName(event.target.value)} placeholder="regiao_comercial" />
            <p className="table-extension-note">{t('tableExtensionNameHint')}</p>{error('field_name')}
          </div>
          <div className="inputWithLabel">
            <label className="label" htmlFor="extension-type">{t('type')}</label>
            <select id="extension-type" className="input" value={type} disabled={locked || saving}
              onChange={(event) => {
                const next = event.target.value as FieldType;
                setType(next);
                setSettings(settingsForType(next));
                setHasDefault(false);
                setDefaultText('');
                setOptionText('');
              }}>
              {options?.field_types.map((fieldType) => (
                <option key={fieldType} value={fieldType}>{t(`tableExtensionTypes.${fieldType}`)}</option>
              ))}
            </select>{error('field_type')}
          </div>
          {(type === 'text' || type === 'long_text') && (
            <div className="inputWithLabel">
              <label className="label" htmlFor="extension-length">{t('tableExtensionMaxLength')}</label>
              <input id="extension-length" className="input" type="number" min={1}
                max={type === 'text' ? 255 : 1000000} value={settings.max_length ?? ''} disabled={locked || saving}
                onChange={(event) => setSettings({ max_length: Number(event.target.value) })} />
            </div>
          )}
          {type === 'decimal' && (
            <>
              <div className="inputWithLabel">
                <label className="label" htmlFor="extension-precision">{t('tableExtensionPrecision')}</label>
                <input id="extension-precision" className="input" type="number" min={1} max={30}
                  value={settings.precision ?? ''} disabled={locked || saving}
                  onChange={(event) => setSettings({ ...settings, precision: Number(event.target.value) })} />
              </div>
              <div className="inputWithLabel">
                <label className="label" htmlFor="extension-scale">{t('tableExtensionScale')}</label>
                <input id="extension-scale" className="input" type="number" min={0} max={10}
                  value={settings.scale ?? ''} disabled={locked || saving}
                  onChange={(event) => setSettings({ ...settings, scale: Number(event.target.value) })} />
              </div>
            </>
          )}
          {(type === 'select' || type === 'multi_select') && (
            <div className="inputWithLabel full-width">
              <label className="label" htmlFor="extension-options">{t('tableExtensionOptions')}</label>
              <textarea id="extension-options" className="input" value={optionText} disabled={locked || saving}
                onChange={(event) => setOptionText(event.target.value)} />
            </div>
          )}
          {error('field_settings') && <div className="full-width">{error('field_settings')}</div>}
          <div className="inputWithLabel full-width">
            <label className="label" htmlFor="extension-description">{t('description')}</label>
            <textarea id="extension-description" className="input" value={description} maxLength={2000} disabled={saving}
              onChange={(event) => setDescription(event.target.value)} />{error('description')}
          </div>
          <label className="table-extension-checkbox" htmlFor="extension-required">
            <input id="extension-required" type="checkbox" checked={required} disabled={saving}
              onChange={(event) => setRequired(event.target.checked)} />{t('tableExtensionRequired')}
          </label>
          <label className="table-extension-checkbox" htmlFor="extension-has-default">
            <input id="extension-has-default" type="checkbox" checked={hasDefault} disabled={saving}
              onChange={(event) => {
                setHasDefault(event.target.checked);
                if (type === 'boolean') setDefaultText('false');
              }} />
            {t('tableExtensionHasDefault')}
          </label>
          {hasDefault && (
            <div className="inputWithLabel full-width">
              <label className="label" htmlFor="extension-default">{t('tableExtensionDefault')}</label>
              {type === 'boolean' || type === 'select' ? (
                <select id="extension-default" className="input" value={defaultText} disabled={saving}
                  onChange={(event) => setDefaultText(event.target.value)}>
                  {type === 'boolean' ? <><option value="false">{t('no')}</option><option value="true">{t('yes')}</option></>
                    : <><option value="">{t('select')}</option>{choices.map((choice, index) => (
                      <option key={`${choice}-${index}`} value={choice}>{choice}</option>
                    ))}</>}
                </select>
              ) : type === 'multi_select' || type === 'long_text' ? (
                <textarea id="extension-default" className="input" value={defaultText} disabled={saving}
                  onChange={(event) => setDefaultText(event.target.value)} />
              ) : (
                <input id="extension-default" className="input" disabled={saving} value={defaultText}
                  type={type === 'date' ? 'date' : type === 'datetime' ? 'datetime-local' : 'text'} step={1}
                  onChange={(event) => setDefaultText(event.target.value)} />
              )}
              <p className="table-extension-note">{t('tableExtensionDefaultHint')}</p>
              {type === 'multi_select' && <p className="table-extension-note">{t('tableExtensionOptions')}</p>}
              {error('default_value')}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default TableExtensionModal;
